/* eslint-disable react/jsx-no-constructed-context-values */

/* Shared */
import { TABLENAMES } from 'application/Shared/Constants';
import { SessionHelper } from 'application/Shared/Helpers';
import { ClientLink } from 'application/Shared/types/clientLink';
import { Device } from 'application/Shared/types/device';
import { Tenant as TenantType } from 'application/Shared/types/tenant';
import { ProviderType } from 'application/Shared/types/tenantProvider';

/* Hooks */
import { useGetClientLinks } from 'hooks/dataAccess/ClientLinks/clientLink';
import { useFetchDevice } from 'hooks/dataAccess/Device/device';
import {
    useSession,
    useSetTenantIsStale,
    useUpdateSession
} from 'hooks/redux/Selectors/session';
import { useTenantInfoByTenantId } from 'hooks/redux/Selectors/tenants';

/*  Objects  */
import Session from 'objects/Session';

/*  React  */
import React, {
    Dispatch,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState
} from 'react';
import { useParams } from 'react-router';

interface ClientDevices {
    [key: string]: number;
}

type ClientApp = Pick<
    ClientLink,
    'applicationBreakdown' | 'uniqueClientApplications'
>;

interface ClientApps {
    [key: string]: ClientApp;
}
interface AugmentedTenant extends Partial<TenantType> {
    providerType: ProviderType | null;
}

export interface PartnerClientsContextType {
    clientApps: ClientApps;
    clientDevices: ClientDevices;
    clientToDelete: ClientLink | null;
    excludeInternal: boolean;
    internalId: string | null;
    reloadSessionPartnerTenantLinks: () => void;
    setClientToDelete: Dispatch<SetStateAction<ClientLink | null>>;
    setShowAddClient: Dispatch<SetStateAction<boolean>>;
    setTenantToEditIntegration: Dispatch<
        SetStateAction<AugmentedTenant | null>
    >;
    showAddClient: boolean;
    tableName: string;
    tableParameters: Record<string, unknown>;
    tenantToEditIntegration: AugmentedTenant | null;
}

export const PartnerClientsTableContext =
    createContext<PartnerClientsContextType>({
        clientApps: {},
        clientDevices: {},
        clientToDelete: null,
        excludeInternal: false,
        internalId: null,
        reloadSessionPartnerTenantLinks: () => {},
        setClientToDelete: () => {},
        setShowAddClient: () => {},
        setTenantToEditIntegration: () => {},
        showAddClient: false,
        tableName: TABLENAMES.PARTNER_CLIENTS,
        tableParameters: {},
        tenantToEditIntegration: null
    });

const parametersClientDevices = {
    excludeExpired: true,
    includeClients: true,
    size: -1
};

export const PartnerClientsTableContextProvider = ({
    children
}: {
    children: React.ReactElement;
}) => {
    const [showAddClient, setShowAddClient] = useState<boolean>(false);
    const [clientToDelete, setClientToDelete] = useState<ClientLink | null>(
        null
    );
    const [tenantToEditIntegration, setTenantToEditIntegration] =
        useState<AugmentedTenant | null>(null);
    const session = useSession();
    const sessionHelper = new SessionHelper(session);
    // Check if User has access to Internal Deployment
    const excludeInternal = sessionHelper.noInternalAccess();

    const { partnerId } = useParams();
    const partnerTenantInfo = useTenantInfoByTenantId(partnerId);
    const partnerTenant = partnerTenantInfo?.tenant;

    const internalId = partnerTenant ? partnerTenant.internalId : null;

    const includePartner = partnerTenant?.id === partnerTenant?.internalId;

    const parametersClientLink = useMemo(
        () => ({
            includeClients: true,
            includePartner,
            includeUniqueApps: true,
            size: -1
        }),
        [includePartner]
    );

    const tableParameters = useMemo(
        () => ({
            excludeInternal,
            includePartner,
            includeProvider: true,
            includeTenant: true,
            includeUserCount: true
        }),
        [excludeInternal, includePartner]
    );

    const { clientLinks } = useGetClientLinks(parametersClientLink);
    const clientApps: ClientApps = {};
    clientLinks.forEach((client) => {
        const { id, applicationBreakdown, uniqueClientApplications } = client;
        clientApps[id] = {
            applicationBreakdown,
            uniqueClientApplications
        };
    });

    const { devices } = useFetchDevice({ parameters: parametersClientDevices });

    const clientDevices = (devices || []).reduce<ClientDevices>(
        (acc, device: Device) => {
            if (acc[device?.platformTenantId]) {
                acc[device?.platformTenantId] += 1;
            } else {
                acc[device?.platformTenantId] = 1;
            }
            return acc;
        },
        {}
    );

    const tableName = TABLENAMES.PARTNER_CLIENTS;

    const updateSession = useUpdateSession();
    const staleTenant = useSetTenantIsStale();

    const reloadSessionPartnerTenantLinks = useCallback(async () => {
        const sessionInfo = await Session.getSessionInfo();
        updateSession(sessionInfo);
        staleTenant(partnerTenant?.id);
    }, [partnerTenant?.id, staleTenant, updateSession]);

    const providerValues = {
        clientApps,
        clientDevices,
        clientToDelete,
        excludeInternal,
        internalId,
        reloadSessionPartnerTenantLinks,
        setClientToDelete,
        setShowAddClient,
        setTenantToEditIntegration,
        showAddClient,
        tableName,
        tableParameters,
        tenantToEditIntegration
    };

    return (
        <PartnerClientsTableContext.Provider value={providerValues}>
            {children}
        </PartnerClientsTableContext.Provider>
    );
};

export const usePartnerClientsTableContext = () => {
    const context = useContext(PartnerClientsTableContext);

    if (context === undefined) {
        throw new Error(
            '`usePartnerClientsTableContext` must be used within `PartnerClientsTableContextProvider`'
        );
    }

    return context;
};
